'use client';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { websitePublicConfig } from '@/config/website-public';
import { buildLocalePathFromCurrentPath } from '@/lib/i18n/locale-path';
import { setLocaleOverrideCookie } from '@/lib/i18n/locale-preference';
import { cn } from '@/lib/ui/utils';
import type { Locale } from 'next-intl';
import Link from 'next/link';
import toolsStyles from './cms-workspace-sidebar-tools.module.css';
import styles from './cms-workspace-sidebar.module.css';

export function SidebarLocaleMenu({
  currentPath,
  locale,
  label,
  onNavigate,
}: {
  currentPath: string;
  locale: string;
  label: string;
  onNavigate?: () => void;
}) {
  return (
    <div className={styles.utilityLanguageShell}>
      <button
        type="button"
        className={toolsStyles.toolButton}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
      >
        {renderAppIcon({ name: 'Globe', className: toolsStyles.icon })}
      </button>
      <div className={styles.utilityLanguagePanel}>
        <div className={styles.localeMenu}>
          <SidebarLocaleOptions
            currentPath={currentPath}
            locale={locale}
            onNavigate={onNavigate}
          />
        </div>
      </div>
    </div>
  );
}

export function SidebarLocaleOptions({
  currentPath,
  locale,
  onNavigate,
}: { currentPath: string; locale: string; onNavigate?: () => void }) {
  return (
    <div className={toolsStyles.localeOptions} role="menu">
      {Object.entries(websitePublicConfig.i18n.locales).map(
        ([localeKey, localeConfig]) => {
          const isCurrentLocale = localeKey === locale;
          return (
            <Link
              key={localeKey}
              href={buildLocalePathFromCurrentPath({
                currentPath,
                locale: localeKey as Locale,
              })}
              prefetch={false}
              className={cn(
                styles.localeMenuItem,
                isCurrentLocale && styles.localeMenuItemActive
              )}
              aria-current={isCurrentLocale ? 'page' : undefined}
              role="menuitem"
              onClick={() => {
                setLocaleOverrideCookie(localeKey as Locale);
                onNavigate?.();
              }}
            >
              <span className={styles.localeMenuText}>{localeConfig.name}</span>
            </Link>
          );
        }
      )}
    </div>
  );
}
